<template>
  <div class="app-container">
    <!-- 非合伙人提示 -->
    <el-alert
      v-if="!isAgent"
      title="您不是推广合伙人，无权访问该页面"
      type="error"
      show-icon
      :closable="false"
      style="margin-bottom: 15px;"
    />

    <!-- 总开关未开启提示 -->
    <el-alert
      v-if="isAgent && !switchOn"
      title="推广合伙人平台总开关未开启（agent_switch=0），创建下级账号已禁用；请在总后台开启后再操作"
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 15px;"
    />

    <!-- 顶部信息 -->
    <div class="agent-info-bar">
      <span>合伙人身份：<b :class="isAgent ? 'ok' : 'no'">{{ isAgent ? '是' : '否' }}</b></span>
      <span>平台总开关：<b :class="switchOn ? 'ok' : 'no'">{{ switchOn ? '已开启' : '未开启' }}</b></span>
      <span>已绑定直接下级：<b>{{ subCount }}</b> 人</span>
      <span>分成比例上限：<b>{{ maxRatio }}%</b></span>
      <el-button size="mini" icon="el-icon-refresh" @click="refreshAgentInfo">刷新状态</el-button>
      <el-button type="primary" size="small" icon="el-icon-plus" :disabled="!canCreate" @click="handleCreate">
        创建下级账号
      </el-button>
    </div>

    <!-- 搜索区域 -->
    <div class="filter-container">
      <div class="filter-row">
        <span class="filter-label">下级UID：</span>
        <el-input v-model="queryParams.sub_uid" placeholder="请输入下级UID" style="width: 200px;" class="filter-item"
          clearable @keyup.enter.native="handleQuery" />
      </div>
      <div class="filter-row">
        <span class="filter-label">状态：</span>
        <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 140px;" class="filter-item">
          <el-option label="启用" :value="1" />
          <el-option label="停用" :value="0" />
        </el-select>
      </div>
      <div class="filter-row button-row">
        <el-button class="filter-item" type="primary" icon="el-icon-search" @click="handleQuery">搜索</el-button>
        <el-button class="filter-item" type="default" icon="el-icon-refresh" @click="resetQuery">重置</el-button>
      </div>
    </div>

    <!-- 表格区域 -->
    <el-table v-loading="listLoading" :data="list" border fit highlight-current-row style="width: 100%;">
      <el-table-column label="序号" type="index" align="center" width="60" />
      <el-table-column label="昵称" align="center">
        <template slot-scope="{row}">
          <span>{{ (row.sub_user && row.sub_user.nick) || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="UID" align="center" width="110">
        <template slot-scope="{row}">
          <span>{{ (row.sub_user && row.sub_user.uid) || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="手机号" align="center" width="130">
        <template slot-scope="{row}">
          <span>{{ (row.sub_user && row.sub_user.mobile) || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="团队人数" align="center" width="90">
        <template slot-scope="{row}">
          <span>{{ row.team_count || 0 }}</span>
        </template>
      </el-table-column>
      <el-table-column label="分成比例" align="center" width="200">
        <template slot-scope="{row}">
          <!-- 行内编辑 -->
          <template v-if="editRowId === row.id">
            <el-input-number v-model="editRatio" :min="0" :max="maxRatio" :precision="2" :step="1" size="small"
              style="width: 110px;" />
            <el-button type="success" size="mini" icon="el-icon-check" :loading="ratioSaving" style="margin-left: 5px;"
              @click="saveRatio(row)" />
            <el-button type="info" size="mini" icon="el-icon-close" style="margin-left: 2px;" @click="cancelEditRatio" />
          </template>
          <template v-else>
            <el-tag type="warning" size="small">{{ row.ratio }}%</el-tag>
            <el-button type="text" size="mini" icon="el-icon-edit" style="margin-left: 5px;"
              @click="startEditRatio(row)">修改</el-button>
          </template>
        </template>
      </el-table-column>
      <el-table-column label="状态" align="center" width="80">
        <template slot-scope="{row}">
          <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
            {{ row.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="备注" prop="remark" align="center" show-overflow-tooltip />
      <el-table-column label="绑定时间" align="center" width="160">
        <template slot-scope="{row}">
          <span>{{ row.create_time || '-' }}</span>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <pagination v-show="total > 0" :total="total" :page.sync="queryParams.page" :limit.sync="queryParams.limit"
        @pagination="getList" />
    </div>

    <!-- 创建下级账号弹窗 -->
    <el-dialog title="创建下级账号" :visible.sync="createDialogVisible" width="500px" :close-on-click-modal="false"
      @close="resetCreateForm">
      <el-form ref="createForm" :model="createForm" :rules="createRules" label-width="90px">
        <el-form-item label="手机号" prop="mobile">
          <el-input v-model="createForm.mobile" placeholder="请输入手机号" maxlength="11" />
          <div class="form-tip">需为未注册过的手机号；已注册的账号须由总后台「绑定下级」处理（先将其上级改为您，再设比例）</div>
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="createForm.password" type="password" placeholder="至少6位" show-password />
        </el-form-item>
        <el-form-item label="昵称" prop="nick">
          <el-input v-model="createForm.nick" placeholder="不填默认为 用户{uid}" />
        </el-form-item>
        <el-form-item label="性别" prop="gender">
          <el-radio-group v-model="createForm.gender">
            <el-radio label="男">男</el-radio>
            <el-radio label="女">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="分成比例" prop="ratio">
          <el-input-number v-model="createForm.ratio" :min="0" :max="maxRatio" :precision="2" :step="1"
            style="width: 180px;" />
          <span class="ratio-tip">%（0 ~ {{ maxRatio }}%，从您的收益中分给该下级）</span>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="createLoading" @click="submitCreate">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import { mapGetters } from 'vuex'
import {
  createSubordinate,
  setSubordinateRatio,
  getAgentSubordinateList
} from '@/api/user'

export default {
  name: 'AgentSubordinate',
  components: { Pagination },
  data() {
    return {
      list: [],
      total: 0,
      listLoading: true,
      queryParams: {
        page: 1,
        limit: 10,
        sub_uid: '',
        status: ''
      },
      // 行内编辑比例
      editRowId: null,
      editRatio: 0,
      ratioSaving: false,
      // 创建下级
      createDialogVisible: false,
      createLoading: false,
      createForm: {
        mobile: '',
        password: '',
        nick: '',
        gender: '男',
        ratio: 0
      },
      createRules: {
        mobile: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1\d{10}$/, message: '手机号格式不正确', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码至少6位', trigger: 'blur' }
        ],
        gender: [
          { required: true, message: '请选择性别', trigger: 'change' }
        ],
        ratio: [
          { required: true, message: '请设置分成比例', trigger: 'blur' }
        ]
      },
      maxRatio: 80
    }
  },
  computed: {
    ...mapGetters([
      'isAgent',
      'agentSwitchOn',
      'agentMaxRatio',
      'agentSubCount'
    ]),
    switchOn() {
      return this.agentSwitchOn
    },
    subCount() {
      return this.agentSubCount
    },
    canCreate() {
      return this.isAgent && this.switchOn
    }
  },
  created() {
    this.maxRatio = this.agentMaxRatio
    if (!this.isAgent) {
      this.listLoading = false
      return
    }
    this.getList()
  },
  methods: {
    async getList() {
      this.listLoading = true
      try {
        const params = {
          page: this.queryParams.page,
          limit: this.queryParams.limit
        }
        if (this.queryParams.sub_uid) {
          params.sub_uid = this.queryParams.sub_uid
        }
        if (this.queryParams.status !== '' && this.queryParams.status !== null && this.queryParams.status !== undefined) {
          params.status = this.queryParams.status
        }

        const res = await getAgentSubordinateList(params)
        if ((res.code === 0 || res.code === 200) && res.data) {
          this.list = res.data.data || []
          this.total = res.data.total || 0
          // 以接口返回的 max_ratio 为准
          if (res.data.max_ratio) {
            this.maxRatio = Number(res.data.max_ratio)
          }
        } else {
          this.$message.error(res.msg || '获取下级列表失败')
          this.list = []
          this.total = 0
        }
      } catch (error) {
        this.$message.error(error.message || '获取下级列表失败，请稍后重试')
        this.list = []
        this.total = 0
      } finally {
        this.listLoading = false
      }
    },

    handleQuery() {
      this.queryParams.page = 1
      this.getList()
    },

    resetQuery() {
      this.queryParams = {
        page: 1,
        limit: 10,
        sub_uid: '',
        status: ''
      }
      this.getList()
    },

    // ========== 行内修改比例 ==========
    startEditRatio(row) {
      this.editRowId = row.id
      this.editRatio = Number(row.ratio) || 0
    },

    cancelEditRatio() {
      this.editRowId = null
      this.editRatio = 0
    },

    async saveRatio(row) {
      if (this.editRatio < 0 || this.editRatio > this.maxRatio) {
        this.$message.error(`分成比例上限为 ${this.maxRatio}%`)
        return
      }
      this.ratioSaving = true
      try {
        const res = await setSubordinateRatio({
          subordinate_id: row.subordinate_id,
          ratio: this.editRatio
        })
        if (res.code === 0 || res.code === 200) {
          this.$message.success('分成比例修改成功')
          row.ratio = this.editRatio
          this.cancelEditRatio()
        } else {
          this.$message.error(res.msg || res.message || '修改失败')
        }
      } catch (error) {
        // 原样展示后端报错（含具体ID，便于排查 pid）
        this.$message.error(error.message || '修改失败')
      } finally {
        this.ratioSaving = false
      }
    },

    // ========== 创建下级 ==========
    // 刷新合伙人状态（排查开关/身份用）
    async refreshAgentInfo() {
      await this.$store.dispatch('agent/getAgentInfo')
      this.maxRatio = this.agentMaxRatio
      if (this.isAgent) {
        this.getList()
      }
      this.$message.info(`is_agent=${this.isAgent ? 1 : 0}，switch_on=${this.switchOn ? 1 : 0}（详见控制台 agentInfo 原始返回）`)
    },

    handleCreate() {
      if (!this.isAgent) {
        this.$message.error('您不是推广合伙人，无法创建下级账号')
        return
      }
      if (!this.switchOn) {
        this.$message.warning('推广合伙人功能未开启（agent_switch=0），请联系平台管理员在总后台开启后再创建下级')
        return
      }
      this.createDialogVisible = true
    },

    resetCreateForm() {
      this.createForm = {
        mobile: '',
        password: '',
        nick: '',
        gender: '男',
        ratio: 0
      }
      this.$nextTick(() => {
        this.$refs.createForm && this.$refs.createForm.clearValidate()
      })
    },

    submitCreate() {
      this.$refs.createForm.validate(async valid => {
        if (!valid) return
        if (this.createForm.ratio < 0 || this.createForm.ratio > this.maxRatio) {
          this.$message.error(`分成比例上限为 ${this.maxRatio}%`)
          return
        }
        this.createLoading = true
        try {
          const data = {
            mobile: this.createForm.mobile.trim(),
            password: this.createForm.password,
            gender: this.createForm.gender,
            ratio: this.createForm.ratio
          }
          if (this.createForm.nick && this.createForm.nick.trim()) {
            data.nick = this.createForm.nick.trim()
          }
          const res = await createSubordinate(data)
          // 注意：后端部分错误也用 code=0 返回（如「该手机号已注册」且 data 为空），
          // 因此成功必须同时满足：code 正确 + data 中有 uid/id
          const created = res.data && (res.data.uid || res.data.id)
          if ((res.code === 0 || res.code === 200) && created) {
            this.$message.success(`下级账号创建成功（UID：${res.data.uid || res.data.id}，分成比例 ${this.createForm.ratio}%）`)
            this.createDialogVisible = false
            // 刷新列表与合伙人信息（sub_count）
            this.getList()
            this.$store.dispatch('agent/getAgentInfo')
          } else {
            this.$message.error(res.msg || res.message || '创建失败')
          }
        } catch (error) {
          this.$message.error(error.message || '创建失败')
        } finally {
          this.createLoading = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.agent-info-bar {
  display: flex;
  align-items: center;
  gap: 25px;
  padding: 12px 20px;
  margin-bottom: 15px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .06);
  font-size: 14px;
  color: #606266;

  b {
    color: #e6a23c;
    font-size: 16px;

    &.ok {
      color: #67c23c;
    }

    &.no {
      color: #f56c6c;
    }
  }

  .el-button {
    margin-left: auto;
  }
}

.filter-container {
  padding-bottom: 10px;

  .filter-row {
    display: inline-block;
    margin-right: 20px;
    margin-bottom: 10px;
    vertical-align: top;

    .filter-label {
      margin-right: 8px;
      min-width: 60px;
      white-space: nowrap;
    }

    .filter-item {
      display: inline-block;
      vertical-align: middle;
      margin-right: 10px;
    }
  }

  .button-row {
    margin-right: 0;
  }
}

.ratio-tip {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}

.form-tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}

.pagination-container {
  text-align: center;
}

// 移动端适配
@media (max-width: 768px) {
  .agent-info-bar {
    flex-wrap: wrap;
    gap: 10px;
    font-size: 13px;

    .el-button {
      margin-left: 0;
    }
  }

  .filter-container {
    .filter-row {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      margin-right: 0;

      .filter-label {
        min-width: 50px;
        font-size: 12px;
      }

      .filter-item {
        flex: 1;
      }
    }

    .el-input,
    .el-select {
      width: 100% !important;
    }
  }

  .el-table {
    font-size: 12px;

    .el-table__cell {
      padding: 5px 2px;
    }
  }
}
</style>
